// @ts-check
import {Country, School, User} from "models"
import {memo, shapeComponent, ShapeComponent, useI18n, View} from "shared/base"
import React, {useMemo} from "react"
import AppHistory from "shared/history"
import Button from "components/chalky-layout/button"
import ChalkyCheckbox from "components/chalky-layout/checkbox"
import ChalkyInput from "components/chalky-layout/input"
import {ChalkyLayoutSelectSchools} from "components/schools/select"
import Devise from "@kaspernj/api-maker/build/devise"
import {digg} from "diggerize"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {Form} from "@kaspernj/api-maker/build/form"
import setLayout from "shared/set-layout"
import updateSession from "components/users/update-session"

// The country the form pre-fills, scopes the school typeahead to, and sends
// as the user's country on submit. Hardcoded to Sweden for the NE iframe;
// future embed origins (other partners, other countries) can vary this.
const SIGNUP_COUNTRY_CODE = "SE"

/**
 * @typedef {object} RoutesEmbedSignUpProps
 */

/**
 * @typedef {object} RoutesEmbedSignUpState
 * @property {boolean} acceptTerms
 * @property {Country | null} signupCountry
 * @property {boolean} submitting
 * @property {object | null} form
 * @property {School | null} selectedSchool
 */

/**
 * @extends {ShapeComponent<RoutesEmbedSignUpProps, RoutesEmbedSignUpState>}
 */
class RoutesEmbedSignUp extends ShapeComponent {
  state = {
    acceptTerms: false,
    signupCountry: null,
    submitting: false,
    form: null,
    selectedSchool: null
  }

  componentDidMount() {
    this.loadSignupCountry()
  }

  loadSignupCountry = async () => {
    const country = await Country.ransack({country_code_eq: SIGNUP_COUNTRY_CODE}).first()

    this.setState({signupCountry: country})
  }

  setup() {
    this.schoolsQuery = useMemo(() => {
      const query = School.ransack({s: "contact_current_translation_name asc"})

      if (this.s.signupCountry) query.ransack({country_id_eq: this.s.signupCountry.id()})

      return query
    }, [this.s.signupCountry?.id()])
  }

  render() {
    setLayout("embed")
    const {t} = useI18n({namespace: "js.routes.embed.sign_up"})

    return (
      <View
        style={this.rootContainerStyle ||= {
          backgroundColor: "#ffffff",
          minHeight: "100vh",
          paddingTop: 32,
          paddingBottom: 32,
          paddingHorizontal: 16
        }}
        testID="routes--embed--sign-up"
      >
        <View
          style={this.formContainerStyle ||= {
            maxWidth: 520,
            width: "100%",
            marginLeft: "auto",
            marginRight: "auto"
          }}
        >
          <Form onSubmit={this.tt.onSubmit} setForm={this.setStates.form}>
            <View style={this.firstNameFieldStyle ||= {marginBottom: 16}}>
              <ChalkyInput
                label={t(".first_name")}
                name="user[contact_attributes][first_name]"
                testID="first-name-input"
              />
            </View>
            <View style={this.lastNameFieldStyle ||= {marginBottom: 16}}>
              <ChalkyInput
                label={t(".last_name")}
                name="user[contact_attributes][last_name]"
                testID="last-name-input"
              />
            </View>
            <View style={this.emailFieldStyle ||= {marginBottom: 16}}>
              <ChalkyInput
                label={t(".email")}
                name="user[email]"
                testID="email-input"
              />
            </View>
            <View style={this.emailConfirmationFieldStyle ||= {marginBottom: 16}}>
              <ChalkyInput
                label={t(".repeat_email")}
                name="user[email_confirmation]"
                testID="email-confirmation-input"
              />
            </View>
            <View style={this.passwordFieldStyle ||= {marginBottom: 16}}>
              <ChalkyInput
                label={t(".password")}
                name="user[password]"
                secureTextEntry
                testID="password-input"
              />
            </View>
            <View style={this.passwordConfirmationFieldStyle ||= {marginBottom: 16}}>
              <ChalkyInput
                label={t(".repeat_password")}
                name="user[password_confirmation]"
                secureTextEntry
                testID="password-confirmation-input"
              />
            </View>
            <View style={this.schoolFieldStyle ||= {marginBottom: 16}}>
              <ChalkyLayoutSelectSchools
                id="school"
                label={t(".search_existing_school")}
                onChange={this.tt.onSchoolChange}
                placeholder={t(".search_by_start_typing")}
                query={this.tt.schoolsQuery}
                testID="school-select"
              />
            </View>
            <View style={this.acceptTermsStyle ||= {marginTop: 8, marginBottom: 16}}>
              <ChalkyCheckbox
                checked={this.s.acceptTerms}
                label={t(".accept_terms")}
                name="user[notification_setting_attributes][accept_terms]"
                onChangeChecked={this.setStates.acceptTerms}
                testID="accept-terms"
              />
            </View>
            <View style={this.buttonRowStyle ||= {marginTop: 16}}>
              <Button
                disabled={this.s.submitting || !this.s.acceptTerms || !this.s.signupCountry || !this.s.selectedSchool}
                onPress={this.tt.onSubmit}
                primary
                testID="submit-button"
                title={t(".create_account")}
              />
            </View>
          </Form>
        </View>
      </View>
    )
  }

  onSchoolChange = ({options}) => this.setState({selectedSchool: digg(options, 0, "school")})

  onSubmit = async () => {
    if (this.s.submitting || !this.s.signupCountry || !this.s.form || !this.s.selectedSchool) return

    this.setState({submitting: true})

    try {
      const data = this.s.form.asObject()

      data.user ||= {}
      data.user.contact_attributes ||= {}
      data.user.contact_attributes.country_id = this.s.signupCountry.id()
      data.school_id = this.s.selectedSchool.id()
      data.start_swedish_trial = true

      const result = await User.createUser(data)
      const user = digg(result, "user")

      await Devise.persistSession()
      await updateSession(user.id())

      AppHistory.push("/embed/se/skapa-konto/valkommen")
    } catch (error) {
      FlashMessage.errorResponse(error)
    } finally {
      this.setState({submitting: false})
    }
  }
}

export default memo(shapeComponent(RoutesEmbedSignUp))
